import { RuntimeCatalog } from "./schema"
import { ContentSnapshot, ContentStore, PreparedContent } from "./store"

interface Binding {
    prepare: (catalog: RuntimeCatalog) => unknown
    apply: (value: any) => void
    current: unknown
}
const copy = <T>(value: T): T => JSON.parse(JSON.stringify(value))

/**
 * Stages dependent tables before activation. Binding callbacks only assign module state;
 * network/rendering work belongs in the coordinator outside this synchronous transaction.
 */
export class RuntimeContent {
    private store: ContentStore
    private view: RuntimeCatalog
    private bindings = new Set<Binding>()
    private pending?: { handle: PreparedContent; values: Map<Binding, unknown>; view: RuntimeCatalog }
    private busy = false

    constructor(manifest: unknown, catalog: unknown) {
        this.store = new ContentStore(manifest, catalog)
        this.view = copy(this.store.active.catalog) as RuntimeCatalog
    }
    get active(): ContentSnapshot { return this.store.active }

    private assertIdle() {
        if (this.busy) throw new Error("Content transaction is already in progress")
    }

    /** Pure selectors share one detached catalog view, preserving existing table aliases. */
    bind<T>(select: (catalog: RuntimeCatalog) => T, apply: (state: T) => void): () => void {
        this.assertIdle()
        const binding: Binding = { prepare: select, apply, current: undefined }
        this.busy = true
        try {
            binding.current = select(this.view)
            apply(binding.current as T)
            this.bindings.add(binding)
            // A newly imported consumer was not included in an earlier preparation.
            this.cancelPending()
        } finally { this.busy = false }
        return () => {
            this.assertIdle()
            if (this.bindings.delete(binding)) this.cancelPending()
        }
    }

    prepare(manifest: unknown, catalog: unknown): PreparedContent {
        this.assertIdle()
        this.busy = true
        try {
            const values = new Map<Binding, unknown>()
            let view: RuntimeCatalog
            const handle = this.store.prepare(manifest, catalog, candidate => {
                view = copy(candidate.catalog) as RuntimeCatalog
                for (const binding of this.bindings) values.set(binding, binding.prepare(view))
            })
            this.pending = { handle, values, view }
            return handle
        } finally { this.busy = false }
    }

    activate(handle: PreparedContent): ContentSnapshot {
        this.assertIdle()
        if (!this.pending || this.pending.handle !== handle) throw new Error("Content preparation is stale or belongs to another runtime")
        const pending = this.pending
        const applied: Binding[] = []
        this.busy = true
        try {
            for (const [binding, value] of pending.values) {
                // Include the failing binding: a callback might assign state before throwing.
                applied.push(binding)
                binding.apply(value)
            }
            const active = this.store.activate(handle)
            this.view = pending.view
            for (const [binding, value] of pending.values) binding.current = value
            this.pending = undefined
            return active
        } catch (error) {
            const failures: unknown[] = []
            for (const binding of applied.reverse()) {
                try { binding.apply(binding.current) } catch (rollbackError) { failures.push(rollbackError) }
            }
            this.cancelPending()
            if (failures.length) throw new Error(`Content rollback failed after ${String(error)}: ${failures.map(String).join("; ")}`)
            throw error
        } finally { this.busy = false }
    }

    discard(handle: PreparedContent): void {
        this.assertIdle()
        if (this.pending?.handle === handle) this.cancelPending()
    }

    private cancelPending() {
        if (this.pending) this.store.discard(this.pending.handle)
        this.pending = undefined
    }
}
